import _ from 'underscore';
import { toIANATimeZone, fromLegacyTimeZone } from '@bingads-webui-universal/time-zone-converter';
import { useMemo } from 'react';
import { colors } from '../styles/color';
import {
  DataPointForChart,
  IDataPointWithDate,
  IPerformanceTimeSeries,
  ISeries,
  MetricTypes,
} from '../types/types';

interface ISeriesWithDate {
  MetricName: string;
  DataPoints: Array<IDataPointWithDate>;
}

function parseAPIDate(date, i18n, ianaTimeZone) {
  const parser = i18n.dateParser({
    raw: 'yyyy-MM-ddTHH:mm:ss',
    timeZone: ianaTimeZone,
  });

  return parser(date.substr(0, 19));
}

function transformPoints(points, i18n, ianaTimeZone): IDataPointWithDate[] {
  return points?.map(({ DateTime, Value, Fluctuate }) => {
    const parsedDate = parseAPIDate(DateTime, i18n, ianaTimeZone);
    return {
      DateTime: parsedDate,
      Value,
      Fluctuate: !!Fluctuate,
    };
  }).filter((i) => i);
}

function getSeriesWithDate(performanceTimeSeries: IPerformanceTimeSeries, i18n, ianaTimeZone): ISeriesWithDate {
  return {
    MetricName: performanceTimeSeries?.MetricName,
    DataPoints: transformPoints(performanceTimeSeries?.DataPoints, i18n, ianaTimeZone),
  };
}

function formatDate(timestamp, i18n, TimeZone): string {
  if (timestamp) {
    return i18n.formatDate(new Date(timestamp), _.extend({
      timeZone: toIANATimeZone(fromLegacyTimeZone(TimeZone)),
    }, { skeleton: 'Md' }));
  }

  return '';
}

function highchartsPoints(points: IDataPointWithDate[], i18n, TimeZone): DataPointForChart[] {
  return points.map((point) => [formatDate(point?.DateTime, i18n, TimeZone), point?.Value, point?.Fluctuate]);
}

function highchartsSeries(series: ISeriesWithDate, metricType, symbol, i18n, TimeZone): Partial<ISeries> {
  if (_.isEmpty(metricType) || metricType === 'None') {
    return {};
  }

  const { DataPoints } = series || {};
  const category = metricType;

  const metricDisplayNames = {
    Conversions: i18n.getString(_TL_('Conv.')),
    Impressions: i18n.getString(_TL_('Impr.')),
    Clicks: i18n.getString(_TL_('Clicks')),
  };

  const marker = {
    enabled: true,
    fillColor: colors.primary,
    lineColor: colors.white,
    lineWidth: 2,
    radius: 6,
    symbol,
    states: {
      hover: {
        enabled: true,
        lineWidth: 2,
        radius: 8,
      },
    },
  };

  return _.isEmpty(DataPoints) ? {} : {
    category,
    name: metricDisplayNames[metricType],
    color: colors.primary,
    data: highchartsPoints(DataPoints, i18n, TimeZone),
    lineWidth: 2,
    dashStyle: 'Solid',
    marker,
    yAxis: 0,
  };
}

export function useChartData({
  performanceTimeSeries,
  i18n,
  metricType,
  TimeZone,
}: {
  performanceTimeSeries: IPerformanceTimeSeries;
  i18n: any;
  metricType: MetricTypes;
  TimeZone: number;
}): Partial<ISeries> {
  return useMemo(() => {
    const ianaTimeZone = toIANATimeZone(fromLegacyTimeZone(TimeZone));
    const seriesWithDate = getSeriesWithDate(performanceTimeSeries, i18n, ianaTimeZone);
    const series = highchartsSeries(seriesWithDate, metricType, 'diamond', i18n, TimeZone);

    return series;
  }, [performanceTimeSeries, i18n, metricType, TimeZone]);
}
